const chapter = {
  id: 'd-and-f-block',
  slug: 'd-and-f-block',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'd- and f-Block Elements',
        hindi: 'd- और f-खण्ड के तत्व',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'd-block-general',
      title: {
        english: 'd-Block — General Trends',
        hindi: 'd-खण्ड — सामान्य प्रवृत्तियाँ',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Transition element',
        hindi: 'संक्रमण तत्व',
          },
          body: {
            english: 'An element whose **atom or its common ions have a partially-filled d subshell** ($d^1$ to $d^9$). Zn, Cd, Hg ($d^{10}$) are placed in d-block but are **not** typical transition elements.',
        hindi: 'एक तत्व जिसका **परमाणु या उसके सामान्य आयनों में आंशिक रूप से भरा हुआ d उपकोश** ($d^1$ से $d^9$) होता है। Zn, Cd, Hg ($d^{10}$) को d-ब्लॉक में रखा गया है, लेकिन ये विशिष्ट संक्रमण तत्व **नहीं** हैं।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'General configuration $(n-1)d^{1-10}\\,ns^{1-2}$. Anomalies: Cr $[Ar]3d^5 4s^1$ and Cu $[Ar]3d^{10} 4s^1$ (extra stability of half-filled & fully-filled d).',
        hindi: 'सामान्य कॉन्फ़िगरेशन $(n-1)d^{1-10}\\,ns^{1-2}$। विसंगतियाँ: Cr $[Ar]3d^5 4s^1$ और Cu $[Ar]3d^{10} 4s^1$ (आधे भरे और पूरी तरह से भरे हुए d की अतिरिक्त स्थिरता)।',
            },
            {
              english: 'Atomic radii decrease then become nearly constant; **lanthanoid contraction** makes 5d radii ≈ 4d radii (Zr ≈ Hf, Nb ≈ Ta).',
        hindi: 'परमाणु त्रिज्या घटती है और फिर लगभग स्थिर हो जाती है; **लैन्थेनॉइड संकुचन** 5d त्रिज्या ≈ 4d त्रिज्या (Zr ≈ Hf, Nb ≈ Ta) बनाता है।',
            },
            {
              english: 'High densities; high MP/BP (max around middle of series — many unpaired e⁻ ⇒ strong metallic bonding); Mn & Tc anomalously low.',
        hindi: 'उच्च घनत्व; उच्च MP/BP (श्रृंखला के मध्य के आसपास अधिकतम - कई अयुग्मित e⁻ ⇒ मजबूत धात्विक बंधन); एमएन एवं टीसी असामान्य रूप से कम है।',
            },
            {
              english: 'High IE (between s- and p-block).',
        hindi: 'उच्च IE (एस- और पी-ब्लॉक के बीच)।',
            },
            {
              english: 'Variable oxidation states — successive in 1s; max in middle (Mn $+7$, Os $+8$).',
        hindi: 'परिवर्तनीय ऑक्सीकरण अवस्थाएँ - 1s में क्रमिक; मध्य में अधिकतम (एमएन $+7$, ओएस $+8$)। **रंगीन आयन** (डी-डी संक्रमण, आंशिक रूप से भरा हुआ डी): Sc³⁺ ($+7$), Ti⁴⁺ ($+8$), Cu⁺ (), Zn²⁺ () रंगहीन हैं। **प्रतिचुम्बकत्व** $+7$ बी.एम. (केवल स्पिन); $+8$.',
            },
            {
              english: '**Coloured ions** (d-d transitions, partly filled d): Sc³⁺ ($d^0$), Ti⁴⁺ ($d^0$), Cu⁺ ($d^{10}$), Zn²⁺ ($d^{10}$) are colourless.',
        hindi: '**रंगीन आयन** (डी-डी संक्रमण, आंशिक रूप से भरे हुए डी): Sc³⁺ ($d^0$), Ti⁴⁺ ($d^0$), Cu⁺ ($d^{10}$), Zn²⁺ ($d^{10}$) रंगहीन हैं।',
            },
            {
              english: '**Paramagnetism** $\\mu = \\sqrt{n(n+2)}$ B.M. (spin-only); n = unpaired e⁻.',
        hindi: '**प्रतिचुम्बकत्व** $\\mu = \\sqrt{n(n+2)}$ बी.एम. (केवल स्पिन); n = अयुग्मित e⁻.',
            },
            {
              english: 'Catalytic activity (V₂O₅, Fe, Pt, Ni, MnO₂) — variable O.S. + adsorption on surface.',
        hindi: 'उत्प्रेरक गतिविधि (V₂O₅, Fe, Pt, Ni, MnO₂) - परिवर्तनीय O.S. + सतह पर सोखना।',
            },
            {
              english: 'Form alloys, **interstitial compounds** (H, C, N, B fit in interstices — TiC, Mn₄N, Fe₃H), and complexes.',
        hindi: 'मिश्रधातु बनाते हैं, **अंतरालीय यौगिक** (H, C, N, B अंतराल में फिट होते हैं - TiC, Mn₄N, Fe₃H), और कॉम्प्लेक्स।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Ion', 'Config', 'n', '$\\mu$ (B.M.)', 'Colour'],
          rows: [
            ['Sc³⁺', '$d^0$', '0', '0', 'Colourless'],
            ['Ti³⁺', '$d^1$', '1', '1.73', 'Purple'],
            ['V³⁺', '$d^2$', '2', '2.84', 'Green'],
            ['Cr³⁺', '$d^3$', '3', '3.87', 'Violet'],
            ['Mn²⁺', '$d^5$', '5', '5.92', 'Pink'],
            ['Fe³⁺', '$d^5$', '5', '5.92', 'Yellow'],
            ['Cu²⁺', '$d^9$', '1', '1.73', 'Blue'],
            ['Zn²⁺', '$d^{10}$', '0', '0', 'Colourless'],
          ],
        },
      ],
    },
    {
      id: 'oxidation-states',
      title: {
        english: 'Oxidation States & Standard Potentials',
        hindi: 'ऑक्सीकरण अवस्थाएँ और मानक विभव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Maximum O.S. of 3d series: Sc(+3), Ti(+4), V(+5), Cr(+6), Mn(+7), Fe(+6), Co(+5), Ni(+4), Cu(+2/+3), Zn(+2).',
        hindi: 'अधिकतम ओएस 3डी श्रृंखला के: Sc(+3), Ti(+4), V(+5), Cr(+6), Mn(+7), Fe(+6), Co(+5), Ni(+4), Cu(+2/+3), Zn(+2)।',
            },
            {
              english: '$E^\\circ_{M^{2+}/M}$ (3d) — generally negative (metals are reducing). Notable: $E^\\circ_{Cu^{2+}/Cu} = +0.34$ V (only +ve) — high $\\Delta_{atom}H + IE$ poorly compensated by $\\Delta_{hyd}H$.',
        hindi: '$E^\\circ_{M^{2+}/M}$ (3डी) - आम तौर पर नकारात्मक (धातुएं कम हो रही हैं)। उल्लेखनीय: $E^\\circ_{Cu^{2+}/Cu} = +0.34$ V (केवल +ve) - उच्च $\\Delta_{परमाणु}H + IE$ को $\\Delta_{hyd}H$ द्वारा खराब मुआवजा दिया गया।',
            },
            {
              english: 'Mn²⁺/Mn most negative ($-1.18$ V) — extra stability of $d^5$.',
        hindi: 'Mn²⁺/Mn सबसे नकारात्मक ($-1.18$ V) - $d^5$ की अतिरिक्त स्थिरता।',
            },
            {
              english: '$E^\\circ_{M^{3+}/M^{2+}}$: Mn³⁺/Mn²⁺ very high ($+1.57$ V — Mn³⁺ unstable, $d^5$ → $d^4$); Fe³⁺/Fe²⁺ moderate ($+0.77$ V — $d^5$ stable); Cr³⁺/Cr²⁺ negative ($d^3$ stable).',
        hindi: '$E^\\circ_{M^{3+}/M^{2+}}$: Mn³⁺/Mn²⁺ बहुत अधिक ($+1.57$ V — Mn³⁺ अस्थिर, $d^5$ → $d^4$); Fe³⁺/Fe²⁺ मध्यम ($+0.77$ V — $d^5$ स्थिर); Cr³⁺/Cr²⁺ नकारात्मक ($d^3$ स्थिर)।',
            },
          ],
        },
      ],
    },
    {
      id: 'kmno4-k2cr2o7',
      title: {
        english: 'KMnO₄ and K₂Cr₂O₇',
        hindi: 'KMnO₄ और K₂Cr₂O₇',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**KMnO₄** — preparation from pyrolusite $MnO_2$:',
        hindi: '**KMnO₄** — पायरोलुसाइट से तैयारी $MnO_2$:',
            },
            {
              english: '$MnO_2 + KOH + O_2 \\to K_2MnO_4 + H_2O$ (fusion);',
        hindi: '$MnO_2 + KOH + O_2 \\to K_2MnO_4 + H_2O$ (फ्यूजन);',
            },
            {
              english: '$3MnO_4^{2-} + 4H^+ \\to 2MnO_4^- + MnO_2 + 2H_2O$ (disproportionation in acid) or electrolytic oxidation.',
        hindi: '$3MnO_4^{2-} + 4H^+ \\to 2MnO_4^- + MnO_2 + 2H_2O$ (एसिड में अनुपातहीनता) या इलेक्ट्रोलाइटिक ऑक्सीकरण।',
            },
            {
              english: 'Structure: $MnO_4^-$ — tetrahedral, Mn in $+7$, $d^0$ (intense purple from charge-transfer transition, not d-d).',
        hindi: 'संरचना: $MnO_4^-$ - टेट्राहेड्रल, $+7$ में Mn, $d^0$ (चार्ज-ट्रांसफर संक्रमण से तीव्र बैंगनी, डी-डी नहीं)।',
            },
            {
              english: 'Strong oxidant. In **acidic** medium: $MnO_4^- + 8H^+ + 5e^- \\to Mn^{2+} + 4H_2O$ ($n = 5$).',
        hindi: 'प्रबल ऑक्सीडेंट. **अम्लीय** माध्यम में: $MnO_4^- + 8H^+ + 5e^- \\to Mn^{2+} + 4H_2O$ ($n = 5$).',
            },
            {
              english: 'In **neutral / weakly alkaline**: $MnO_4^- + 2H_2O + 3e^- \\to MnO_2 + 4OH^-$ ($n = 3$).',
        hindi: '**तटस्थ / कमजोर क्षारीय** में: $MnO_4^- + 2H_2O + 3e^- \\to MnO_2 + 4OH^-$ ($n = 3$)।',
            },
            {
              english: 'In **strongly alkaline**: $MnO_4^- + e^- \\to MnO_4^{2-}$ ($n = 1$).',
        hindi: '**अत्यधिक क्षारीय** में: $MnO_4^- + e^- \\to MnO_4^{2-}$ ($n = 1$).',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**K₂Cr₂O₇** from chromite $FeCr_2O_4$:',
        hindi: '**K₂Cr₂O₇** क्रोमाइट से $FeCr_2O_4$:',
            },
            {
              english: '$4FeCr_2O_4 + 8Na_2CO_3 + 7O_2 \\to 8Na_2CrO_4 + 2Fe_2O_3 + 8CO_2$;',
        hindi: '$4FeCr_2O_4 + 8Na_2CO_3 + 7O_2 \\to 8Na_2CrO_4 + 2Fe_2O_3 + 8CO_2$;',
            },
            {
              english: '$2Na_2CrO_4 + 2H^+ \\to Na_2Cr_2O_7 + 2Na^+ + H_2O$;',
        hindi: '$2Na_2CrO_4 + 2H^+ \\to Na_2Cr_2O_7 + 2Na^+ + H_2O$;',
            },
            {
              english: '$Na_2Cr_2O_7 + 2KCl \\to K_2Cr_2O_7 + 2NaCl$ (less soluble).',
        hindi: '$Na_2Cr_2O_7 + 2KCl \\to K_2Cr_2O_7 + 2NaCl$ (कम घुलनशील)।',
            },
            {
              english: 'Equilibrium: $2CrO_4^{2-} + 2H^+ \\rightleftharpoons Cr_2O_7^{2-} + H_2O$ (orange ↔ yellow).',
        hindi: 'संतुलन: $2CrO_4^{2-} + 2H^+ \\rightleftharpoons Cr_2O_7^{2-} + H_2O$ (नारंगी ↔ पीला)।',
            },
            {
              english: 'Acidic K₂Cr₂O₇: $Cr_2O_7^{2-} + 14H^+ + 6e^- \\to 2Cr^{3+} + 7H_2O$ ($n = 6$).',
        hindi: 'अम्लीय K₂Cr₂O₇: $Cr_2O_7^{2-} + 14H^+ + 6e^- \\to 2Cr^{3+} + 7H_2O$ ($n = 6$)।',
            },
            {
              english: 'Used as primary standard in iodometric / Mohr titration.',
        hindi: 'आयोडोमेट्रिक/मोहर अनुमापन में प्राथमिक मानक के रूप में उपयोग किया जाता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'lanthanoids',
      title: {
        english: 'f-Block — Lanthanoids',
        hindi: 'f-खण्ड — लैन्थेनॉइड',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Ce (Z = 58) to Lu (Z = 71); 14 elements after La. Configuration $[Xe]\\,4f^{1-14}\\,5d^{0-1}\\,6s^2$.',
        hindi: 'सीई (जेड = 58) से लू (जेड = 71); ला के बाद 14 तत्व। कॉन्फ़िगरेशन $[Xe]\\,4f^{1-14}\\,5d^{0-1}\\,6s^2$।',
            },
            {
              english: 'Most stable & common O.S. = **+3**. Other: Ce⁺⁴ (good oxidant; ammonium ceric sulphate), Eu²⁺ (4f⁷ stable, reductant), Yb²⁺ (4f¹⁴), Tb⁴⁺.',
        hindi: 'सबसे स्थिर और सामान्य O.S. = **+3**. अन्य: Ce⁺⁴ (अच्छा ऑक्सीडेंट; अमोनियम सेरिक सल्फेट), Eu²⁺ (4f⁷ स्थिर, रिडक्टेंट), Yb²⁺ (4f¹⁴), Tb⁴⁺। **लैन्थेनॉइड संकुचन** - ला से लू तक परमाणु और आयनिक त्रिज्या में लगातार कमी (4एफ इलेक्ट्रॉनों द्वारा खराब परिरक्षण)। परिणाम:',
            },
            {
              english: '**Lanthanoid contraction** — steady decrease in atomic & ionic radii from La to Lu (poor shielding by 4f electrons). Consequences:',
        hindi: '**लैन्थेनॉइड संकुचन** - ला से लू तक परमाणु और आयनिक त्रिज्या में लगातार कमी (4f इलेक्ट्रॉनों द्वारा खराब परिरक्षण)। नतीजे:',
            },
            {
              english: '• Similarity of properties of Ln³⁺ ions ⇒ difficult to separate.',
        hindi: '• Ln³⁺ आयनों के गुणों की समानता ⇒ अलग करना मुश्किल।',
            },
            {
              english: '• Atomic radii of 5d ≈ 4d (Zr ≈ Hf etc.).',
        hindi: '• 5d ≈ 4d (Zr ≈ Hf आदि) की परमाणु त्रिज्या।',
            },
            {
              english: '• Increase in electronegativity & basicity decreases down the series ($La(OH)_3$ most basic, $Lu(OH)_3$ least).',
        hindi: '• इलेक्ट्रोनगेटिविटी और बुनियादीता में वृद्धि श्रृंखला में नीचे की ओर घटती जाती है ($La(OH)_3$ सबसे बुनियादी, $Lu(OH)_3$ सबसे कम)।',
            },
            {
              english: 'Uses: Misch metal (95 % Ln + 5 % Fe) — flints; Ce in self-cleaning oven; Nd magnets.',
        hindi: 'उपयोग: मिश मेटल (95 % Ln + 5 % Fe) — चकमक पत्थर; स्व-सफाई ओवन में सीई; एनडी मैग्नेट.',
            },
          ],
        },
      ],
    },
    {
      id: 'actinoids',
      title: {
        english: 'f-Block — Actinoids (Brief)',
        hindi: 'f-खण्ड — ऐक्टिनॉइड (संक्षिप्त)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Th (Z = 90) to Lr (Z = 103). Configuration $[Rn]\\,5f^{1-14}\\,6d^{0-1}\\,7s^2$.',
        hindi: 'Th (Z = 90) से Lr (Z = 103)। कॉन्फ़िगरेशन $[Rn]\\,5f^{1-14}\\,6d^{0-1}\\,7s^2$.',
            },
            {
              english: 'All radioactive; transuranic (after U) are man-made.',
        hindi: 'सभी रेडियोधर्मी; ट्रांसयूरेनिक (यू के बाद) मानव निर्मित हैं।',
            },
            {
              english: 'Show **wider range of O.S.** than lanthanoids (5f, 6d, 7s have similar energies): U shows +3 to +6, common +6.',
        hindi: 'लैंथेनॉइड्स की तुलना में **ओएस की व्यापक रेंज** दिखाएं (5एफ, 6डी, 7एस में समान ऊर्जाएं हैं): यू +3 से +6, सामान्य +6 दिखाता है। **एक्टिनॉइड संकुचन** लैंथेनॉइड संकुचन (5f परिरक्षण और भी खराब) से अधिक है।',
            },
            {
              english: '**Actinoid contraction** is greater than lanthanoid contraction (5f shielding even poorer).',
        hindi: '**एक्टिनॉइड संकुचन** लैंथेनॉइड संकुचन से अधिक है (5f परिरक्षण और भी खराब)।',
            },
            {
              english: 'Th, U used in nuclear reactors.',
        hindi: 'Th, U का उपयोग परमाणु रिएक्टरों में किया जाता है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\mu = \\sqrt{n(n+2)}\\ \\text{B.M.}',
      desc: {
        english: 'Spin-only magnetic moment',
        hindi: 'केवल स्पिन चुंबकीय क्षण',
      },
    },
    {
      latex: 'MnO_4^- + 8H^+ + 5e^- \\to Mn^{2+} + 4H_2O',
      desc: {
        english: 'Acidic KMnO₄',
        hindi: 'अम्लीय KMnO₄',
      },
    },
    {
      latex: 'Cr_2O_7^{2-} + 14H^+ + 6e^- \\to 2Cr^{3+} + 7H_2O',
      desc: {
        english: 'Acidic K₂Cr₂O₇',
        hindi: 'अम्लीय K₂Cr₂O₇',
      },
    },
    {
      latex: '2CrO_4^{2-} + 2H^+ \\rightleftharpoons Cr_2O_7^{2-} + H_2O',
      desc: {
        english: 'Yellow ↔ orange',
        hindi: 'पीला ↔ नारंगी',
      },
    },
    {
      latex: 'Cr:[Ar]3d^54s^1,\\ Cu:[Ar]3d^{10}4s^1',
      desc: {
        english: 'Anomalous configs',
        hindi: 'असंगत कॉन्फ़िगरेशन',
      },
    },
    {
      latex: 'Zr \\approx Hf,\\ Nb \\approx Ta',
      desc: {
        english: 'Lanthanoid contraction',
        hindi: 'लैंथेनॉइड संकुचन',
      },
    },
    {
      latex: 'E^\\circ_{Cu^{2+}/Cu} = +0.34\\ \\text{V}',
      desc: {
        english: 'Only +ve in 3d',
        hindi: '3डी में केवल +ve',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Magnetic',
      problem: {
        english: 'Spin-only magnetic moment of Fe³⁺ ($d^5$):',
        hindi: 'Fe³⁺ का स्पिन-केवल चुंबकीय क्षण ($d^5$):',
      },
      answer: {
        english: '5.92 B.M.',
        hindi: '5.92 बी.एम.',
      },
      solution: {
        english: '$\\sqrt{5(7)}$.',
        hindi: '$\\sqrt{5(7)}$।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Lanthanoid',
      problem: {
        english: 'Most stable oxidation state of lanthanoids:',
        hindi: 'लैंथेनॉइड्स की सबसे स्थिर ऑक्सीकरण अवस्था:',
      },
      answer: {
        english: '+3',
        hindi: '+3',
      },
      solution: {
        english: 'Loss of 5d¹ + 6s² + one 4f.',
        hindi: '5d¹ + 6s² + एक 4f का नुकसान।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'KMnO₄',
      problem: {
        english: 'n-factor of KMnO₄ in neutral medium:',
        hindi: 'तटस्थ माध्यम में KMnO₄ का n-कारक:',
      },
      answer: {
        english: '3',
        hindi: '3',
      },
      solution: {
        english: '$Mn^{+7} \\to Mn^{+4}$.',
        hindi: '$Mn^{+7} \\to Mn^{+4}$।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Config',
      problem: {
        english: 'Element with config $[Ar]3d^54s^1$:',
        hindi: 'कॉन्फ़िगरेशन $[Ar]3d^54s^1$ वाला तत्व:',
      },
      answer: {
        english: 'Cr',
        hindi: 'करोड़',
      },
      solution: {
        english: 'Half-filled d stability.',
        hindi: 'आधा भरा हुआ डी स्थिरता।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Contraction',
      problem: {
        english: 'Cause of similarity in atomic radii of Zr and Hf:',
        hindi: 'Zr और Hf की परमाणु त्रिज्या में समानता का कारण:',
      },
      answer: {
        english: 'Lanthanoid contraction',
        hindi: 'लैंथेनॉइड संकुचन',
      },
      solution: {
        english: 'Poor 4f shielding.',
        hindi: 'खराब 4एफ परिरक्षण।',
      },
    },
  ],
  next: {
    slug: 'coordination-compounds',
    title: 'Coordination Compounds',
  },
};

export default chapter;
